// The calls strip in Blueprint (calls round, step 2; DESIGN.md §9): under the top bar on every page,
// for those who see calls. It reads the recent calls and again on any call event, and shows each one
// ringing, then answered on a phone while it runs (strip-model.ts). Open the call goes to its page on
// Calls; Not now takes it off this person's strip. Answer, Hold, Transfer and End sit behind the phone
// seam (phone.ts): off, since calls ring people's phones. Riverstone keeps its ringing banner.
import { useEffect, useRef, useState } from 'react';
import { CallAction, CallRow, CallStrip } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import { onLiveChange } from '../connection/live.ts';
import type { Call } from '../types.ts';
import { usePhone, type PhoneAct } from './phone.ts';
import { CALL_EVENTS } from './ringing-model.ts';
import {
  callLine,
  callerName,
  callerWho,
  ringingSaid,
  sinceWords,
  stripRows,
  stripState,
} from './strip-model.ts';

export function CallsStripHost({
  openId,
  onCalls,
  onOpen,
}: {
  /** The call open on Calls (it's followed, and needs no Open). */
  openId: string | null;
  /** Calls is the page open. */
  onCalls: boolean;
  onOpen: (callId: string) => void;
}) {
  const phone = usePhone();
  const [calls, setCalls] = useState<Call[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  // the calls this person follows: seen ringing here, or opened
  const [followed, setFollowed] = useState<ReadonlySet<string>>(new Set());
  const [said, setSaid] = useState('');
  const wasRinging = useRef<Set<string>>(new Set());
  const [settled, setSettled] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const read = () =>
      void client
        .call<{ calls: Call[] }>('calls.recent', { limit: 15 })
        .then((r) => {
          if (!live) return;
          setCalls(r.calls);
          setNow(Date.now());
        })
        .catch(() => undefined);
    read();
    const stop = onLiveChange(({ type }) => {
      if (!CALL_EVENTS.test(type)) return;
      clearTimeout(timer);
      timer = setTimeout(read, 150);
    });
    return () => {
      live = false;
      clearTimeout(timer);
      stop();
    };
  }, []);

  useEffect(() => {
    if (openId) setFollowed((f) => (f.has(openId) ? f : new Set([...f, openId])));
  }, [openId]);

  const rows = stripRows(calls, { now, dismissed, followed });
  const ringingIds = rows.filter((r) => r.kind === 'ringing').map((r) => r.call.id);
  const ringingKey = ringingIds.join(',');

  // a call seen ringing is followed; a new one is read out at once; one that was ringing and is now
  // answered plays the settle once
  useEffect(() => {
    const ids = ringingKey ? ringingKey.split(',') : [];
    const fresh = ids.filter((id) => !wasRinging.current.has(id));
    if (fresh.length) {
      setFollowed((f) => new Set([...f, ...fresh]));
      const c = calls.find((x) => x.id === fresh[fresh.length - 1]);
      if (c) setSaid(ringingSaid(c));
    }
    const answered = [...wasRinging.current].find(
      (id) => !ids.includes(id) && calls.some((c) => c.id === id && c.status === 'in_progress'),
    );
    if (answered) setSettled(answered);
    wasRinging.current = new Set(ids);
  }, [ringingKey, calls]);

  // the clock on each row, while there are any
  const any = rows.length > 0;
  useEffect(() => {
    if (!any) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [any]);

  if (!rows.length) return null;
  const act = (what: PhoneAct, id: string) => phone.act?.(what, id);
  const notNow = (id: string) => setDismissed((d) => new Set([...d, id]));
  return (
    <CallStrip label="Calls" announce={said} urgent>
      {rows.map((r) => {
        const c = r.call;
        const here = onCalls && openId === c.id;
        const open = here ? null : (
          <CallAction variant={phone.controls ? 'link' : 'primary'} onPress={() => onOpen(c.id)}>
            Open the call
          </CallAction>
        );
        return (
          <CallRow
            key={c.id}
            kind={r.kind}
            state={stripState(r)}
            name={callerName(c)}
            who={callerWho(c)}
            line={callLine(c)}
            time={sinceWords(c.startedAt, now)}
            settle={settled === c.id && r.kind === 'taken'}
            actions={
              <>
                {phone.controls && r.kind === 'ringing' && (
                  <>
                    <CallAction variant="primary" onPress={() => act('answer', c.id)}>
                      Answer
                    </CallAction>
                    <CallAction variant="control" onPress={() => act('answer-hold', c.id)}>
                      Answer and hold
                    </CallAction>
                    <CallAction onPress={() => act('voicemail', c.id)}>Send to voicemail</CallAction>
                  </>
                )}
                {phone.controls && r.kind === 'taken' && (
                  <>
                    <CallAction variant="control" onPress={() => act('hold', c.id)}>
                      Hold
                    </CallAction>
                    <CallAction variant="control" onPress={() => act('transfer', c.id)}>
                      Transfer…
                    </CallAction>
                    <CallAction variant="control" onPress={() => act('end', c.id)}>
                      End call
                    </CallAction>
                  </>
                )}
                {open}
                <CallAction onPress={() => notNow(c.id)}>Not now</CallAction>
              </>
            }
          />
        );
      })}
    </CallStrip>
  );
}
